'use client';

import { useEffect, useState } from 'react';
import { Heart } from 'lucide-react';
import { toast } from '@/lib/toast';
import { useWishlistStore } from '@/stores/wishlist';

interface Props {
  productId: number;
  /** Labeled variant — for PDP next to "Jämför" button. Default is round icon-only. */
  labeled?: boolean;
}

/**
 * Small heart toggle for product grids. Calls the existing wishlist API when
 * the user is logged in; otherwise prompts with a sign-in toast (keeps state
 * authoritative server-side, no localStorage drift).
 */
export function WishlistToggleButton({ productId, labeled }: Props) {
  // Samma fel som i ProductCard: metoden är en stabil referens, listan är det
  // som ändras. Prenumerera på den, annars fylls aldrig hjärtat.
  const iListan = useWishlistStore((s) => s.ids.has(productId));
  const toggle = useWishlistStore((s) => s.toggle);

  const [mounted, setMounted] = useState(false);
  useEffect(() => setMounted(true), []);

  const inWishlist = mounted && iListan;

  const handleClick = async (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();

    /* Ingen inloggningsspärr — se stores/wishlist.ts. Listan fungerar utan
       konto och slås ihop med kontots om kunden loggar in senare. */
    try {
      const added = await toggle(productId);
      toast.success(added ? 'Tillagd i önskelistan' : 'Borttagen från önskelistan');
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Något gick fel');
    }
  };

  if (labeled) {
    return (
      <button
        type="button"
        onClick={handleClick}
        aria-pressed={inWishlist}
        className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium border transition-all ${
          inWishlist
            ? 'bg-red-50 border-red-200 text-red-600'
            : 'bg-white border-gray-200 text-gray-700 hover:border-red-200 hover:text-red-600'
        }`}
      >
        <Heart className={`h-4 w-4 ${inWishlist ? 'fill-current' : ''}`} />
        {inWishlist ? 'I önskelistan' : 'Önskelista'}
      </button>
    );
  }

  return (
    <button
      type="button"
      onClick={handleClick}
      aria-label={inWishlist ? 'Ta bort från önskelistan' : 'Lägg till i önskelistan'}
      aria-pressed={inWishlist}
      className={`inline-flex items-center justify-center h-8 w-8 rounded-full border bg-white/90 backdrop-blur-sm transition-all hover:scale-110 ${
        inWishlist
          ? 'border-red-200 text-red-600'
          : 'border-gray-200 text-gray-500 hover:text-red-600 hover:border-red-200'
      }`}
    >
      <Heart className={`h-4 w-4 ${inWishlist ? 'fill-current' : ''}`} />
    </button>
  );
}
